import { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, X, Star, StarOff, Trash2, Loader2, Image as ImageIcon } from 'lucide-react';
import { getImageUrls } from '@/hooks/useImageUpload';
import { useUploadStore, type UploadItem } from '@/stores/uploadStore';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import type { PropertyImage } from '@/types';
import { cn } from '@/lib/utils';

interface ImageGalleryProps {
  propertyId: string;
  images: PropertyImage[];
  onRefresh: () => void;
  canEdit?: boolean;
}

// Komponente für einzelnes Bild mit Lazy Loading
function LazyImage({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className="w-full h-full">
      {isInView ? (
        <>
          {!isLoaded && (
            <div className="absolute inset-0 bg-muted animate-pulse flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
            </div>
          )}
          <img
            src={src}
            alt={alt}
            className={cn(className, !isLoaded && 'opacity-0')}
            onLoad={() => setIsLoaded(true)}
          />
        </>
      ) : (
        <div className="w-full h-full bg-muted flex items-center justify-center">
          <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
        </div>
      )}
    </div>
  );
}

// Komponente für Upload-Vorschau mit Fortschritt
function UploadingImagePreview({ item }: { item: UploadItem }) {
  return (
    <div className="relative aspect-square rounded-lg overflow-hidden bg-muted">
      {item.previewUrl && (
        <img
          src={item.previewUrl}
          alt={item.fileName}
          className={cn(
            'w-full h-full object-cover transition-opacity',
            item.status === 'error' ? 'opacity-50' : 'opacity-70'
          )}
        />
      )}

      {/* Overlay mit Status */}
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30">
        <Loader2 className="w-8 h-8 text-white animate-spin mb-2" />
        <span className="text-xs text-white">
          {item.status === 'processing' ? 'Verarbeite...' : 'Lade hoch...'}
        </span>
      </div>

      {/* Fortschrittsbalken */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${item.progress}%` }}
        />
      </div>
    </div>
  );
}

export function ImageGallery({ propertyId, images, onRefresh, canEdit = true }: ImageGalleryProps) {
  const [imagesWithUrls, setImagesWithUrls] = useState<PropertyImage[]>([]);
  const [isLoadingUrls, setIsLoadingUrls] = useState(true);
  const [selectedImage, setSelectedImage] = useState<PropertyImage | null>(null);
  const [imageToDelete, setImageToDelete] = useState<PropertyImage | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Globaler Upload Store
  const addUpload = useUploadStore(state => state.addUpload);
  const uploads = useUploadStore(state => state.uploads);

  // Nur Bild-Uploads für diese Property filtern
  const pendingUploads = uploads.filter(
    u => u.type === 'image' &&
      u.propertyId === propertyId &&
      (u.status === 'pending' || u.status === 'uploading' || u.status === 'processing')
  );

  const isUploading = pendingUploads.length > 0;

  // Refresh wenn Uploads abgeschlossen
  const prevCompletedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const completed = uploads.filter(
      u => u.type === 'image' && u.propertyId === propertyId && u.status === 'done'
    );

    const newCompleted = completed.filter(u => !prevCompletedRef.current.has(u.id));
    if (newCompleted.length > 0) {
      onRefresh();
      newCompleted.forEach(u => prevCompletedRef.current.add(u.id));
    }
  }, [uploads, propertyId, onRefresh]);

  // URLs laden (optimiert mit Batch + Cache)
  useEffect(() => {
    let isMounted = true;

    async function loadUrls() {
      if (images.length === 0) {
        setImagesWithUrls([]);
        setIsLoadingUrls(false);
        return;
      }

      setIsLoadingUrls(true);

      const withUrls = await getImageUrls(images);
      if (isMounted) {
        setImagesWithUrls(withUrls);
        setIsLoadingUrls(false);
      }
    }

    loadUrls();
    return () => { isMounted = false; };
  }, [images]);

  // Upload-Funktion
  const handleUploadFiles = useCallback((files: File[]) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));

    imageFiles.forEach(file => {
      addUpload({
        type: 'image',
        fileName: file.name,
        propertyId,
        file,
        previewUrl: URL.createObjectURL(file),
      });
    });
  }, [addUpload, propertyId]);

  // Drag & Drop Handler
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleUploadFiles(files);
  }, [handleUploadFiles]);

  // File Input Handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleUploadFiles(files);
    e.target.value = '';
  };

  // Delete Handler
  const handleDelete = async () => {
    if (!imageToDelete) return;

    try {
      await supabase.storage
        .from('property-images')
        .remove([imageToDelete.storage_path]);

      await supabase
        .from('property_images')
        .delete()
        .eq('id', imageToDelete.id);

      onRefresh();
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setImageToDelete(null);
    }
  };

  // Set Primary Handler
  const handleSetPrimary = async (imageId: string) => {
    try {
      await supabase
        .from('property_images')
        .update({ is_primary: false })
        .eq('property_id', propertyId);

      await supabase
        .from('property_images')
        .update({ is_primary: true })
        .eq('id', imageId);

      onRefresh();
    } catch (err) {
      console.error('Set primary error:', err);
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      {canEdit && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
            isDragging ? 'border-primary bg-primary/5' : 'border-border'
          )}
        >
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            className="hidden"
            id="image-upload"
          />
          <label
            htmlFor="image-upload"
            className="flex flex-col items-center cursor-pointer"
          >
            {isUploading ? (
              <Loader2 className="w-8 h-8 text-primary animate-spin mb-2" />
            ) : (
              <Upload className="w-8 h-8 text-muted-foreground mb-2" />
            )}
            <span className="text-sm text-muted-foreground">
              {isUploading
                ? `${pendingUploads.length} ${pendingUploads.length === 1 ? 'Bild wird' : 'Bilder werden'} hochgeladen...`
                : 'Bilder hierher ziehen oder klicken'}
            </span>
            <span className="text-xs text-muted-foreground mt-1">
              Max. 5MB pro Bild
            </span>
          </label>
        </div>
      )}

      {/* Gallery */}
      {isLoadingUrls && images.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((img) => (
            <div key={img.id} className="aspect-square bg-muted rounded-lg animate-pulse flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
            </div>
          ))}
        </div>
      ) : imagesWithUrls.length > 0 || pendingUploads.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {/* Bilder die gerade hochgeladen werden */}
          {pendingUploads.map((item) => (
            <UploadingImagePreview key={item.id} item={item} />
          ))}

          {/* Vorhandene Bilder */}
          {imagesWithUrls.map((image) => (
            <div
              key={image.id}
              className="group relative aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer"
              onClick={() => setSelectedImage(image)}
            >
              <LazyImage
                src={image.url!}
                alt={image.file_name}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />

              {/* Primary Badge */}
              {image.is_primary && (
                <div className="absolute top-2 left-2 px-2 py-1 bg-primary rounded-full text-xs font-medium text-primary-foreground">
                  Hauptbild
                </div>
              )}

              {/* Actions Overlay */}
              {canEdit && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetPrimary(image.id);
                    }}
                    title={image.is_primary ? 'Ist Hauptbild' : 'Als Hauptbild setzen'}
                  >
                    {image.is_primary ? (
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    ) : (
                      <StarOff className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImageToDelete(image);
                    }}
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
          <p className="text-sm">Noch keine Bilder vorhanden</p>
        </div>
      )}

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={selectedImage.url!}
            alt={selectedImage.file_name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!imageToDelete}
        onClose={() => setImageToDelete(null)}
        onConfirm={handleDelete}
        title="Bild löschen"
        description="Möchtest du dieses Bild wirklich löschen?"
      />
    </div>
  );
}
